import moment from "moment";

import { PortfolioInboxes, PreventiveRecallTab } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PortfolioPageRegistryComponentId } from "@/Portfolio/Components/Extensions/PortfolioPageRegistry";
import { IConsultationRequestInboxModel } from "@/Portfolio/Models/UI/IConsultationRequestsInboxModel";
import { ILabRequestsInboxModel } from "@/Portfolio/Models/UI/IRequestsInboxModel";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PortfolioTabHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_NotesInbox, this.switchToNotesInbox);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_ConsultationRequestsInbox, this.switchToConsultationRequestsInbox);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_RequestsInbox, this.switchToRequestsInbox);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_APSS, this.switchToApss);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PendingApprovals, this.switchToPendingApprovals);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_SentFaxInbox, this.switchToSentFaxInbox);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_FailedFaxes, this.switchToFailedFaxes);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PortalPendingItems, this.switchToPendingPortalItems);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PortalPendingErrorItems, this.switchToPendingPortalErrorItems);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PortalSyncItems, this.switchToSyncedPortalItems);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_Extension, this.switchToExtension);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PreventiveActivitiesInbox, this.switchToPreventiveActivitiesInbox);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PopulationBasedFunding, this.switchToPopulationBasedFunding);
        this.RegisterOperation(LocalOperations.Portfolio.ResetCache, this.resetCache);
        this.RegisterOperation(LocalOperations.Portfolio.SwitchTo_PreventiveRecallList, this.switchToRecallList);
    }

    private switchToNotesInbox(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.Notes);
        store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
    }

    private switchToConsultationRequestsInbox(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.ConsultationRequests);
        store.ConsultationRequestsInboxModel = store.getInitializedMedicalRequestInboxSettings(store.ConsultationRequestsInboxModel, cacheStore.UserSettings?.ConsultationRequestsSearchSettings, "ConsultationRequestPriority") as IConsultationRequestInboxModel;
        store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
    }

    private switchToRequestsInbox(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.Requests);
        store.RequestsInboxModel = store.getInitializedMedicalRequestInboxSettings(store.RequestsInboxModel, cacheStore.UserSettings?.RequestsSearchSettings, "LabRequestPriority") as ILabRequestsInboxModel;
        store.PortfolioActions.LoadMyRequests(store.RequestsInboxModel);
    }

    private switchToSentFaxInbox(request: LocalEvent, store: PortfolioStore): void {
        this.switchToFaxTab(store, PortfolioInboxes.Faxes);
    }

    private switchToFailedFaxes(request: LocalEvent, store: PortfolioStore): void {
        this.switchToFaxTab(store, PortfolioInboxes.FailedFaxes);
    }

    private switchToFaxTab(store: PortfolioStore, inbox: PortfolioInboxes): void {
        store.Model = store.Model.set("CurrentInbox", inbox);

        if (inbox == PortfolioInboxes.Faxes && !store.sentFaxInboxModel.get("Initialized")) {
            store.sentFaxInboxModel = store.sentFaxInboxModel.set("SelectAllStatusChecked", true);
            store.initializeFaxPortfolioInbox(inbox);
        }
        if (inbox == PortfolioInboxes.FailedFaxes && !store.failedFaxInboxModel.get("Initialized")) {
            store.failedFaxInboxModel = store.failedFaxInboxModel.set("SelectAllStatusChecked", false);
            store.initializeFaxPortfolioInbox(inbox);
        }
        store.PortfolioActions.LoadPortfolioFaxes(store.getActiveFaxModel());
    }

    private switchToPendingPortalItems(_: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PortalPendingItems);

        if (!store.portalSyncModel.get("Initialized"))
            store.initializePortalSyncModel();
        store.portalSyncModel = store.portalSyncModel.set("GetErrorOnly", false);
    }

    private switchToPendingPortalErrorItems(_: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PortalSyncItemsErrors);

        if (!store.portalSyncModel.get("Initialized"))
            store.initializePortalSyncModel();
        store.portalSyncModel = store.portalSyncModel.set("GetErrorOnly", true);
    }

    private switchToSyncedPortalItems(_: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PortalSyncItems);

        if (!store.portalSyncModel.get("Initialized"))
            store.initializePortalSyncModel();
    }

    private switchToApss(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model
            .set("CurrentInbox", request.Get<PortfolioInboxes>("inboxType"));
    }

    private switchToPendingApprovals(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PendingApprovals);

        store.LoadPendingApprovalSitesFromCache();
        store.LoadPendingApprovalClinicianUICache();
        store.InitializePendingApprovalStatus();
    }

    private switchToExtension(request: LocalEvent, store: PortfolioStore): void {
        const extensionId = request.Get<PortfolioPageRegistryComponentId>("extensionId");
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.Extension);
        store.Model = store.Model.set("CurrentExtensionId", extensionId);
    }

    private switchToPreventiveActivitiesInbox(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PreventiveActivities);

        store.PortfolioActions.loadPreventiveActivities();
    }

    private switchToPopulationBasedFunding(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PopulationBasedFunding);
    }

    private resetCache(request: LocalEvent, store: PortfolioStore): void {
        store.sentFaxInboxModel = store.sentFaxInboxModel.set("DateTo", moment().toDate());
    }

    private switchToRecallList(request: LocalEvent, store: PortfolioStore): void {
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PreventiveRecallList);
        store.preventiveRecallListModel = store.preventiveRecallListModel.set("CurrentTab", PreventiveRecallTab.Default);
        store.PortfolioActions.loadPreventiveRecallList();
    }
}

export { PortfolioTabHandler };
